import Chip from '@material-ui/core/Chip';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { formatDate, isNil, LoanProcess, PORTAL_COLORS, shouldHideTaskUploadTab } from '@roc/feature-utils';
import { Badge, Box, darken, Grid, Menu, Tooltip, Typography, useTheme, withStyles } from '@material-ui/core';
import { MoreVert, Person, Check, Clear, Redo, ArrowUpwardRounded } from '@material-ui/icons';
import {
  ConfirmDialog,
  DataGrid,
  GridColumnProps,
  IconButton,
  LinkColumn,
  MenuItem,
  StatusChip,
  StatusType,
} from '@roc/ui';
import { formatCurrency } from '@roc/ui/utils';
import { observer } from 'mobx-react';
import { useEffect, useMemo, useState } from 'react';
import { useGeneralTasksStore } from '../hooks/useGeneralTasksStore';
import {
  GeneralTasksFilters,
  GeneralTasksTab,
  TaskGridRow,
  TaskScope,
  TaskStatus,
} from '../types/generalTasksTypes';
import { taskTypeOptions } from '../utils/generalTasksConstants';
import { DescriptionHtmlCell } from './generalTaskDescriptionCell';
import { GeneralTaskLoanTooltip } from './generalTaskLoanTooltip';
import { GeneralTaskLoanFasttrackTooltip } from './taskList/generalTaskLoanFasttrackTooltip';
import { SubtaskIcon } from './taskList/subtaskIcon';
import { useBaseStore, useUserStore } from '@roc/feature-app-core';
import { useLoanRoutes } from '@roc/feature-loans-routes-config';
import { useDocumentStore } from '@roc/feature-documents';
import { TodoTitleLink } from 'libs/feature-loan-details/src/loanDetails/components/cards/todoActions';
import { DocumentTitleCell } from './taskList/documentTitleCell';
import { AssignToDialog, AssignToOwnerDialog, CustomTaskDialog, GeneralTasksStore } from '@roc/feature-general-tasks';
import { LineItemUpload, todoStatusMapping } from '@roc/feature-loan-details';
import { SupportTasksStore } from '../stores/supportTasksStore';
import { GeneralTasksGridStore } from '../stores/generalTasksGridStore';
import { SupportTasksGridStore } from '../stores/supportTasksGridStore';

export const StyledBadge = withStyles(theme => ({
  badge: {
    right: -12,
    top: 13,
    border: `2px solid ${theme.palette.background.paper}`,
    padding: '0 4px',
    fontSize: '11px',
    backgroundColor: PORTAL_COLORS.pendingDocuments,
    color: 'white',
    fontWeight: 700,
    userSelect: 'none',
  },
}))(Badge);

const useStyles = makeStyles((theme: Theme) => ({
  outlined: {
    borderWidth: 1,
    borderStyle: 'solid',
    backgroundColor: 'white',
    color: darken('#D32F2F', 0.2),
    borderColor: '#D32F2F',
    marginTop: '10px',
    marginLeft: '5px',
  },
  link: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',

    '& a': {
      color: theme.palette.primary.dark,
      fontSize: '14px',
      fontWeight: 'bold',
      textDecoration: 'none',
    },
  },
}));

type TaskListViewStores = {
  tasksStore: GeneralTasksStore | SupportTasksStore;
  tasksGridStore: GeneralTasksGridStore | SupportTasksGridStore;
};

type TaskListViewProps = {
  overrideStores?: TaskListViewStores;
};

export const TaskListView = observer(({ overrideStores }: TaskListViewProps) => {
  const { globalStore } = useBaseStore();
  const { generalTasksStore } = useGeneralTasksStore();
  const { editTaskStore, tab, loanId, taskListGridStore } = generalTasksStore;
  const [menu, setMenu] = useState<{ el: HTMLElement; row: TaskGridRow }>(null);
  const isLoanTasksView = !!loanId;
  const { userStore } = useUserStore();
  const { loanRoutesConfig } = useLoanRoutes();
  const [selectedTask, setSelectedTask] = useState<TaskGridRow>(null);
  const userRole = userStore?.userInfo?.role;
  const isInternalPortal = globalStore.isInternalPortal;
  const { documentStore } = useDocumentStore();
  const classes = useStyles();
  const { userInformation } = userStore;
  const [userInfoReady, setUserInfoReady] = useState(false);
  const [openLineItemChat, setOpenLineItemChat] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const theme = useTheme();

  const hasOverride = Boolean(overrideStores);
  const gridStore = overrideStores?.tasksGridStore ?? taskListGridStore;
  const taskStore = overrideStores?.tasksStore ?? generalTasksStore;

  useEffect(() => {
    const fetchUserData = async () => {
      await userStore.getUserInfoAndEntitlements();
      setUserInfoReady(true);
    }
    fetchUserData();
  }, [])

  useEffect(() => {
    if (!hasOverride && userInfoReady) {
      onTabChange();
    }
  }, [tab, userInfoReady]);

  const onTabChange = async () => {
    taskListGridStore.reset();
    await generalTasksStore.resetFilters();
    if (tab === GeneralTasksTab.ALL_TASKS) {
      generalTasksStore.setFilter(GeneralTasksFilters.ASSIGNEE, [{
        label: `${userInformation?.firstName} ${userInformation?.lastName}`,
        value: userInformation?.userId,
      }]);
    }

    generalTasksStore.fetchLoanDataForPage();
  };

  const onPageChange = async pageNumber => {
    await gridStore.setPageNumber(pageNumber);
    generalTasksStore.fetchLoanDataForPage();
  };

  const taskScopeList: TaskScope[] | undefined =
    taskStore.filters[GeneralTasksFilters.TASK_SCOPE]
      ?.map(option => option.value);

  const showReportedByMe = taskScopeList?.includes(TaskScope.REPORTED_BY_ME) || taskScopeList?.includes(TaskScope.WATCHED_BY_ME);

  const columns: GridColumnProps[] = useMemo(() => {
    return taskListGridStore.getTaskGridColumns(userRole, hasOverride, isLoanTasksView, tab, showReportedByMe, isInternalPortal, taskScopeList);
  }, [taskListGridStore, userRole, hasOverride, isLoanTasksView, tab, showReportedByMe, isInternalPortal]);

  const getUrl = (loanId) => {
    return userStore.isNportalUser
      ? '/nportal/#/loan_information/' + loanId
      : loanRoutesConfig.loans(loanId).children.dashboard.url
  }

  const init = async row => {
    const loanId = row.loanId;
    await documentStore.loanStore.fetchLoanDetails(loanId);
    generalTasksStore.setLoanId(loanId);
  };

  const onClose = () => {
    if (generalTasksStore.tab !== GeneralTasksTab.ALL_TASKS_FOR_LOAN) {
      generalTasksStore.setLoanId(null);
    }
    generalTasksStore.applyFilters();
  };

  const frameworkComponents = {
    dateCellRenderer(params) {
      return <>{formatDate(params.value)}</>;
    },
    currencyCellRenderer(params) {
      return <>{params.value ? formatCurrency(params.value) : null}</>;
    },
    statusCellRenderer(params) {
      return (
        !!params.value && (
          <StatusChip
            statusType={StatusType.GENERAL_TASK_STATUS}
            label={TaskStatus[params.value]}
            size="small"
          />
        )
      );
    },
    assigneeCellRenderer(params) {
      return (
        <>
          {params.value && (
            <Chip
              style={{ maxWidth: '100%', textOverflow: 'ellipsis' }}
              icon={<Person />}
              variant="outlined"
              size="small"
              label={params.value}
            />
          )}
        </>
      );
    },
    taskIdUnreadMessagesCellRenderer(params) {
      const row: TaskGridRow = params.node.data;
      return (
        <StyledBadge badgeContent={params.node.data?.unreadCountMessages}>
          {row?.document ?
            <Typography
              style={{
                fontSize: '14px',
                fontWeight: 'bold',
                textDecoration: 'underline',
              }}
            >
              <TodoTitleLink
                document={documentStore.formatDocument(
                  row.document,
                  LoanProcess.UNDERWRITING
                )}
                loanProcess={LoanProcess.UNDERWRITING}
                init={() => init(row)}
                showUploadLink={true}
                onClose={() => onClose()}
                onUploadCustom={() => {
                  setSelectedDocument(row?.document);
                  setOpenLineItemChat(true);
                }}
                propertyId={row?.document?.collateralId}
                style={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  color: theme.palette.primary.dark,
                  fontFamily: 'sans-serif',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  textDecoration: 'underline'
                }}
              >
                {row?.document.taskId}
              </TodoTitleLink>
            </Typography>
            :
            <LinkColumn
              value={params.value}
              url={'#'}
              onClick={() => {
                editTaskStore.openEditTask(row?.taskId);
              }}
              textUnderline={true}
            />
          }
        </StyledBadge>
      );
    },
    taskIdCellRenderer(params) {
      const row: TaskGridRow = params.node.data;
      if (row?.document) {
        return (
          <Typography
            style={{
              fontSize: '14px',
              fontWeight: 'bold',
              textDecoration: 'underline',
            }}
          >
            <TodoTitleLink
              document={documentStore.formatDocument(
                row.document,
                LoanProcess.UNDERWRITING
              )}
              loanProcess={LoanProcess.UNDERWRITING}
              init={() => init(row)}
              showUploadLink={true}
              onClose={() => onClose()}
              propertyId={row?.document?.collateralId}
              style={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                color: theme.palette.primary.dark,
                fontFamily: 'sans-serif',
                fontSize: '14px',
                fontWeight: 'bold',
                textDecoration: 'underline'
              }}
            >
              {params.value}
            </TodoTitleLink>
          </Typography>
        )
      }
      return (
        <LinkColumn
          value={params.value}
          url={'#'}
          onClick={() => {
            editTaskStore.openEditTask(row?.taskId);
          }}
          textUnderline={true}
        />
      );
    },
    titleCellRenderer(params) {
      const row: TaskGridRow = params.node.data;
      if (row?.document) {
        return <DocumentTitleCell row={row} />
      }
      return (
        <Box display="flex" alignItems="center">
          <Box pr={2}>
            <SubtaskIcon isSubtask={!!row?.parentTaskId} />
          </Box>
          <LinkColumn
            value={params.value}
            url={'#'}
            onClick={() => {
              editTaskStore.openEditTask(row?.taskId);
            }}
            textUnderline={true}
          />
        </Box>
      );
    },
    escalatedCellRenderer(params) {
      if (!isNil(params.value)) {
        return (
          <Box style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <Tooltip title="Escalated" style={{
              border: '2px solid rgb(217, 68, 82)',
              borderRadius: '14px',
            }}>
              <ArrowUpwardRounded style={{ color: 'rgb(217, 68, 82)' }} />
            </Tooltip>
          </Box>
        );
      }
      return null;
    },
    descriptionCellRenderer(params) {
      const row: TaskGridRow = params.node.data;
      return <DescriptionHtmlCell html={params.value} />;
    },
    actionCellRenderer(params) {
      const row: TaskGridRow = params.node.data;
      return (
        <IconButton
          testId="open-menu"
          onClick={e => {
            setMenu({
              el: e.currentTarget,
              row: params.node.data,
            });
            setSelectedTask(params.node.data);
          }}
        >
          <MoreVert color="primary" />
        </IconButton>
      );
    },
    booleanCellRenderer(params) {
      return <>{params.value ? 'Yes' : 'No'}</>;
    },
    loanIdCellRenderer(params) {
      const row = params.node.data;
      return (
        <>
          {params.value ? (
            <Box alignItems={'center'} display="flex">
              <GeneralTaskLoanTooltip loanId={row?.loanId}>
                <LinkColumn
                  value={params.value}
                  url={'#'}
                  onClick={e => {
                    open(getUrl(params.value));
                    e.preventDefault();
                  }}
                  textUnderline={true}
                />
              </GeneralTaskLoanTooltip>
              <GeneralTaskLoanFasttrackTooltip loanId={row?.loanId} />
            </Box>
          ) : null}
        </>
      );
    },
    typeCellRenderer(params) {
      const row = params.node.data;
      return row?.parentTaskId ? (
        <Tooltip title="Subtask">
          <Box>S</Box>
        </Tooltip>
      ) : (
        <Tooltip title="Task">
          <Box>T</Box>
        </Tooltip>
      );
    },
    timeElapsedCellRenderer(params) {
      const row = params.node.data;
      const timeElapsed: number = row?.timeElapsed;
      if (timeElapsed) {
        const days = Math.trunc(timeElapsed / 86400);
        const hours = Math.trunc((timeElapsed % 86400) / 3600);
        return (days > 0 ? `${days}D ` : '') + `${hours}H`;
      } else {
        return null;
      }
    },
    categoryCellRenderer(params) {
      const row = params.node.data;
      const taskType = row?.taskType;
      return (
        <Grid container direction='row' spacing={1} xs={12}>
          <Box style={{ overflow: 'hidden', textOverflow: 'ellipsis', marginLeft: '3px' }}>
            {taskTypeOptions.find(opt => taskType === opt.value)?.label}
          </Box>
          {'ESCALATION' === params.value &&
            <Chip variant='outlined'
              size='small'
              label={<Typography variant="h6" style={{ fontWeight: "bold", fontSize: '15px' }}>SOS</Typography>}
              className={classes.outlined} />
          }
        </Grid>
      );
    },
    priorityCellRenderer(params) {
      return (
        !!params.value && (
          <StatusChip
            statusType={StatusType.PRIORITY}
            label={params.value}
            size="small"
          />
        )
      );
    },
    loanStatusCellRenderer: ({ value }) =>
      value ? (
        <StatusChip label={value} size="small" statusType={StatusType.LOAN} />
      ) : null,
    loanSubStatusCellRenderer: ({ value }) =>
      value ? (
        <StatusChip label={value} size="small" statusType={StatusType.SUB_STATUS} />
      ) : null
  };

  const onSortChanged = params => {
    const sortModel = params.api.getSortModel();
    gridStore.setSortModel(sortModel);
  };

  const onActionCompletedHandler = () => {
    gridStore.refresh();
  }

  let title = "";
  let section = "";

  if (selectedDocument) {
    title = documentStore.getDisplayDocumentName(
      selectedDocument?.taskName,
      selectedDocument);

    section = selectedDocument.borrowerId
      ? `${selectedDocument.borrowerName} ${selectedDocument.sectionName}`
      : selectedDocument.collateralId
        ? `${selectedDocument.collateralAddress} ${selectedDocument.sectionName}`
        : selectedDocument.sectionName;
  }

  const formattedTitle = `${title} - ${section}`;

  return (
    <>
      <Box flex="1" display="flex" minHeight="310px">
        <DataGrid
          key={`${tab} ${showReportedByMe} ${userRole} ${isLoanTasksView}`}
          columns={columns}
          rows={gridStore.gridData.data.rows}
          frameworkComponents={frameworkComponents}
          onSortChanged={onSortChanged}
          isLoading={gridStore.isLoading}
          sortModel={gridStore.sortModel}
          filterModel={gridStore.filterModel}
          paginationData={gridStore.gridData.meta}
          setPageNumber={onPageChange}
          domLayout="normal"
        />
        <TaskActionsMenu open={!!menu} anchorEl={menu?.el} onClose={() => setMenu(null)} task={selectedTask} onActionCompleted={onActionCompletedHandler} />
      </Box>
      {openLineItemChat && (
        <CustomTaskDialog
          title={formattedTitle}
          taskId={selectedDocument?.taskId}
          open={openLineItemChat}
          dialogContent={
            (shouldHideTaskUploadTab(selectedDocument) || selectedDocument?.parentTask) ? null : (
              <LineItemUpload document={documentStore.formatDocument(
                selectedDocument,
                LoanProcess.UNDERWRITING
              )} expand={true} />
            )
          }
          handleClose={() => {
            setOpenLineItemChat(false);
            onClose();
          }}
        />
      )}
    </>
  );
});

interface TaskActionsMenuProps {
  open: boolean;
  anchorEl: any;
  onClose: () => void;
  task: TaskGridRow;
  onActionCompleted: () => void;
}

enum CONFIRM_MODAL_ACTION {
  ASSIGN_TO_OWNER,
  MARK_AS_COMPLETED,
  MARK_AS_NOT_APPLICABLE,
  ESCALATE_TASK,
};

const useActionsMenuStyles = makeStyles(() => ({
  icon: {
    paddingRight: 5,
  },
}));

export const TaskActionsMenu = ({ open, anchorEl, onClose, task, onActionCompleted }: TaskActionsMenuProps) => {
  const classes = useActionsMenuStyles();
  const { generalTasksStore } = useGeneralTasksStore();
  const { globalStore } = useBaseStore();
  const [confirmModalAction, setConfirmModalAction] = useState<CONFIRM_MODAL_ACTION>(null);
  const [showAssignToModal, setShowAssignToModal] = useState<boolean>(false);
  const [showAssignToOwnerModal, setShowAssignToOwnerModal] = useState<boolean>(false);
  const isLoanTask = !!task?.loanTaskId;
  const isInternalPortal = globalStore.isInternalPortal;


  const getConfirmModalBody = () => {
    let action = '';
    if (confirmModalAction === CONFIRM_MODAL_ACTION.ASSIGN_TO_OWNER) {
      action = 'assign the task to the owner';
    } else if (confirmModalAction === CONFIRM_MODAL_ACTION.MARK_AS_COMPLETED) {
      action = 'mark the task as completed';
    } else if (confirmModalAction === CONFIRM_MODAL_ACTION.MARK_AS_NOT_APPLICABLE) {
      action = 'mark the task as not applicable';
    } else if (confirmModalAction === CONFIRM_MODAL_ACTION.ESCALATE_TASK) {
      action = 'escalate this task'
    }

    return <Typography>Please confirm you want to {action}.</Typography>
  }


  const handleSubmitConfirmModal = async () => {
    if (confirmModalAction != null) {
      if (confirmModalAction === CONFIRM_MODAL_ACTION.ESCALATE_TASK) {
        generalTasksStore.escalateTask(task.taskId, onActionCompleted);

      } else {
        // status update cases

        let status;
        if (confirmModalAction === CONFIRM_MODAL_ACTION.ASSIGN_TO_OWNER) {
          if (task.status === TaskStatus.NOT_APPLICABLE) {
            status = todoStatusMapping['Pending'];//we don't differentiate status here, this only applies to loan tasks
          } else {
            status = todoStatusMapping['More Info Needed'];//we don't differentiate status here, this only applies to loan tasks
          }
        } else if (confirmModalAction === CONFIRM_MODAL_ACTION.MARK_AS_COMPLETED) {
          status = isLoanTask ? todoStatusMapping['Accepted'] : TaskStatus.ACCEPTED;
        } else if (confirmModalAction === CONFIRM_MODAL_ACTION.MARK_AS_NOT_APPLICABLE) {
          status = isLoanTask ? todoStatusMapping['Not Applicable'] : TaskStatus.NOT_APPLICABLE;
        }

        await generalTasksStore.updateTaskStatusV2(task.taskId, status, onActionCompleted);
      }
    }
    setConfirmModalAction(null);
  }

  const handleCloseAssignToModal = () => {
    setShowAssignToModal(false);
    onActionCompleted && onActionCompleted();
  }

  const handleCloseAssignToOwnerModal = () => {
    setShowAssignToOwnerModal(false);
    onActionCompleted && onActionCompleted();
  }

  const selectedDocument = task?.document;
  const isClosingConditionNotAccepted = !!selectedDocument?.loanTaskClosingConditionId && task?.status !== TaskStatus.ACCEPTED;

  const isParentTask = !!(selectedDocument?.parentTask);

  let showAssignToOwnerAction = false;
  let showMarkAsCompletedAction = false;
  let showMarkAsNotApplicableAction = false;
  const showAssignToAction = isInternalPortal && !isParentTask;
  const showEscalatedTask = !isClosingConditionNotAccepted && !!task && isNil(task?.escalated);

  if (isLoanTask) {//loan task
    showAssignToOwnerAction = isInternalPortal && !isClosingConditionNotAccepted
      && !selectedDocument?.internalTask
      && TaskStatus.PENDING !== task.status && TaskStatus.MORE_INFO_NEEDED !== task.status;

    showMarkAsCompletedAction = isInternalPortal && !isClosingConditionNotAccepted &&
      task.status !== TaskStatus.ACCEPTED && !isParentTask;

    showMarkAsNotApplicableAction = isInternalPortal && !isClosingConditionNotAccepted &&
      task.status !== TaskStatus.NOT_APPLICABLE && !isParentTask;
  } else {//non loan task
    showAssignToOwnerAction = false;
    showMarkAsCompletedAction = isInternalPortal && task?.status !== TaskStatus.ACCEPTED && !isParentTask;
    showMarkAsNotApplicableAction = isInternalPortal && task?.status !== TaskStatus.NOT_APPLICABLE && !isParentTask;
  }

  return (
    <>
      <Menu
        id="menu-appbar"
        getContentAnchorEl={null}
        anchorEl={anchorEl}
        keepMounted
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        open={open}
        onClose={onClose}
      >
        {showAssignToOwnerAction && (
          <MenuItem
            testId="assign-to-owner"
            onClick={() => {
              onClose();
              setShowAssignToOwnerModal(true);
            }}>
            <Redo className={classes.icon} />
            Assign to Sales
          </MenuItem>)}
        {showMarkAsCompletedAction && (
          <MenuItem
            testId="mark-as-completed"
            onClick={() => {
              onClose();
              setConfirmModalAction(CONFIRM_MODAL_ACTION.MARK_AS_COMPLETED);
            }}
          >
            <Check className={classes.icon} />
            Mark as Completed
          </MenuItem>)}
        {showMarkAsNotApplicableAction && (
          <MenuItem
            testId="mark-as-not-applicable"
            onClick={() => {
              onClose();
              setConfirmModalAction(CONFIRM_MODAL_ACTION.MARK_AS_NOT_APPLICABLE);
            }}
          >
            <Clear className={classes.icon} />
            Mark as Not Applicable
          </MenuItem>)}
        {showAssignToAction && (
          <MenuItem
            testId="assign-to"
            onClick={() => {
              onClose();
              setShowAssignToModal(true);
            }}
          >
            <Redo className={classes.icon} />
            Assign to
          </MenuItem>)
        }
        {showEscalatedTask && (
          <MenuItem
            testId="assign-to"
            onClick={() => {
              onClose();
              setConfirmModalAction(CONFIRM_MODAL_ACTION.ESCALATE_TASK);
            }}
          >
            <ArrowUpwardRounded className={classes.icon} />
            Escalate Task
          </MenuItem>
        )
        }
      </Menu>
      <ConfirmDialog
        handleConfirm={handleSubmitConfirmModal}
        handleClose={() => setConfirmModalAction(null)}
        handleCancel={() => setConfirmModalAction(null)}
        open={confirmModalAction !== null}
        body={getConfirmModalBody()}
        confirmButtonText="Confirm"
        cancelButtonText="Close"
        title={'Confirmation'}
      />
      <AssignToDialog
        open={showAssignToModal}
        loanId={task?.loanId}
        taskId={task?.taskId}
        handleClose={handleCloseAssignToModal}
      />
      <AssignToOwnerDialog
        open={showAssignToOwnerModal}
        currentStatus={task?.status}
        taskId={task?.taskId}
        handleClose={handleCloseAssignToOwnerModal}
      />
    </>);
}